import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { blockCreate } from "@shared/schemas/calendar";
import { errorMessage } from "@web/lib/api";
import { validateCalendarBlock } from "@shared/validation/calendar";
import { evaluation, parseInput, type ValidationEvaluation } from "@shared/validation/issues";
import { useFormValidation } from "@web/lib/formValidation";
import { useMe } from "@web/lib/session";
import { api, venuesQuery } from "@web/lib/queries";
import { BottomSheet, Button, Picker, Stack, Text, TextArea, TextField, UnsavedChanges, ValidationSummary, useToast } from "@web/ui";

export type BlockSheetProps = { open: boolean; onClose: () => void; initialDate?: string | null; initialVenueId?: string | null };

/** Block dates on a venue (closed / maintenance) → `POST /api/calendar/blocks`. */
export function BlockSheet({ open, onClose, initialDate, initialVenueId }: BlockSheetProps) {
  const toast = useToast();
  const me = useMe();
  const venues = useQuery({ ...venuesQuery(), enabled: open });
  const [venueId, setVenueId] = useState(initialVenueId ?? "");
  const [kind, setKind] = useState<"blocked" | "maintenance">("blocked");
  const [startDate, setStartDate] = useState(initialDate ?? "");
  const [endDate, setEndDate] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const effectiveVenue = venueId || (venues.data?.length === 1 ? venues.data[0].id : "");
  const [savedResult, setSavedResult] = useState<ValidationEvaluation | null>(null);
  const input = { venueId: effectiveVenue, kind, startDate, endDate: endDate || undefined, note: note || null };
  const parsed = parseInput(blockCreate, input, { type: "calendar_block" }, "calendar_block.reserve");
  const local = validateCalendarBlock({ ...input, endDate: endDate || startDate, timezone: me.organization.timezone }, { action: "calendar_block.reserve" });
  const validation = useFormValidation(evaluation([...parsed.issues, ...local.issues], false));
  const busy = validation.busy;
  const edit = (change: () => void) => validation.edit(() => { setError(null); change(); });
  const dirty = !savedResult && Boolean(venueId !== (initialVenueId ?? "") || kind !== "blocked" || startDate !== (initialDate ?? "") || endDate || note);

  async function save() {
    if (!parsed.success) { validation.report(parsed.issues); return; }
    if (!validation.begin("calendar_block.reserve")) return;
    setError(null);
    try {
      const created = await api.createBlock(parsed.data);
      toast.success("Dates blocked");
      if (created.issues.length) setSavedResult(created); else onClose();
    } catch (e) {
      validation.reject(e);
      setError(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <><UnsavedChanges dirty={dirty} pending={busy} /><BottomSheet
      open={open}
      onClose={() => validation.edit(onClose)}
      title="Block dates"
      footer={
        <Button loading={busy} onClick={savedResult ? onClose : save}>
          {savedResult ? "Done" : "Block dates"}
        </Button>
      }
    >
      {savedResult ? <Stack gap={3}><Text variant="headline">Closure saved · review nearby plans</Text><Text variant="callout" tone="secondary">The closure is saved. These advisories identify tentative bookings or tours that need staff follow-up.</Text><ValidationSummary result={savedResult} saved /></Stack> : <Stack gap={4}>
        <Text variant="caption" tone="secondary">Full-day closure in {me.organization.timezone}; the final reserved day is included.</Text>
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        <Picker label="Venue" placeholder="Choose a venue" value={effectiveVenue} {...validation.field("venueId")} onChange={value => edit(() => setVenueId(value))} options={(venues.data ?? []).map((v) => ({ value: v.id, label: v.name }))} />
        <Picker label="Kind" value={kind} {...validation.field("kind")} onChange={value => edit(() => setKind(value as "blocked" | "maintenance"))} options={[{ value: "blocked", label: "Closed" }, { value: "maintenance", label: "Maintenance" }]} />
        <TextField label="From" type="date" value={startDate} {...validation.field("startDate")} onChange={event => edit(() => setStartDate(event.target.value))} />
        <TextField label="To" type="date" value={endDate} min={startDate || undefined} {...validation.field("endDate")} onChange={event => edit(() => setEndDate(event.target.value))} />
        <Text variant="caption" tone="secondary">Leave the final day empty for a single-day closure.</Text>
        {error && <Text tone="danger">{error}</Text>}
        <TextArea label="Note" value={note} {...validation.field("note")} onChange={event => edit(() => setNote(event.target.value))} />
      </Stack>}
    </BottomSheet></>
  );
}
